/**
 * Générateur — Attendu 198 — O8 « Il construit une figure en créant un motif et
 * en le reproduisant à l'aide d'une boucle. »
 * Niveau : 4ème | Thème #12 Algorithmique (O8, Niveau 3) | Session B.
 * Source prof : O8 MUET en texte ; critère canonique = les « Exemples de réussite »
 * de l'image page 2 (frise reproduite par un bloc + FIGURE DE CARRÉS EMBOÎTÉS).
 * → BANQUE construite sur ce critère.
 *
 * ADAPTATIONS scoring : les tailles successives → trous saisie (suite arithm.) ;
 * la figure → QCM concret (carrés emboîtés vs spirale) ; la frise → comptage
 * calculé.
 *
 * 5 cartes :
 *  1. carres_valeurs — tailles successives des carrés emboîtés (trous saisie).
 *  2. carres_grand   — côté du plus grand carré (saisie).
 *  3. carres_figure  — quelle figure trace le script ? (QCM).
 *  4. frise_motifs   — combien de motifs pour couvrir la largeur ? (saisie).
 *  5. motif_bloc     — quel bloc placer dans la boucle pour reproduire le motif ? (QCM).
 */

import {
    randInt, randomChoice, makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { svgScript, imgElement } from './algo';

const ATTENDU_ID = 198;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VALEURS = 'Carrés emboîtés : les tailles successives';
const L_GRAND = 'Carrés emboîtés : le plus grand';
const L_FIGURE = 'Carrés emboîtés : quelle figure ?';
const L_FRISE = 'La frise : combien de motifs ?';
const L_MOTIF_BLOC = 'La frise : le bon bloc dans la boucle';

// script des carrés emboîtés (côté croissant)
const scriptCarres = (s0, p, n) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'variable', txt: `mettre côté à ${s0}` },
    { t: 'repeat', n, body: [
        { t: 'move', txt: 'dessiner un carré de côté « côté »' },
        { t: 'move', txt: 'revenir au centre' },
        { t: 'variable', txt: `ajouter ${p} à côté` },
    ] },
]);

// ═════════ Carte 1 — tailles successives (trous saisie) ═════════

const genCarresValeurs = () => {
    const s0 = randInt(1, 4) * 10;
    const p = randInt(1, 3) * 10;
    const n = randInt(4, 6);
    const vals = Array.from({ length: n }, (_, i) => s0 + i * p);
    const phrase = 'Côtés successifs des carrés : ' + vals.map((v) => `{{${v}}}`).join(' ; ') + '.';
    const texte = '<p><b>Le lutin dessine des carrés emboîtés : à chaque tour, il trace un carré '
        + 'puis agrandit le côté. Donne les côtés successifs (dans l\'ordre).</b></p>';
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_VALEURS), mode: 'saisie', elements_additionnels: imgElement(scriptCarres(s0, p, n)) });
    payload._meta.params = { s0, p, n };
    return payload;
};

// ═════════ Carte 2 — le plus grand carré (saisie) ═════════

const genCarresGrand = () => {
    const s0 = randInt(1, 4) * 10;
    const p = randInt(1, 3) * 10;
    const n = randInt(4, 8);
    const grand = s0 + (n - 1) * p;
    const texte = '<p><b>Le lutin dessine des carrés emboîtés (le côté augmente à chaque tour). '
        + 'Quel est le côté du plus grand carré tracé ?</b></p>';
    const payload = makeSaisie(String(grand), texte,
        { ...opts(L_GRAND), elements_additionnels: imgElement(scriptCarres(s0, p, n)) });
    payload._meta.params = { s0, p, n, grand };
    return payload;
};

// ═════════ Carte 3 — quelle figure ? (QCM) ═════════

const genCarresFigure = () => {
    const s0 = randInt(1, 4) * 10;
    const p = randInt(1, 3) * 10;
    const n = randInt(4, 6);
    const correct = 'Des carrés emboîtés (concentriques).';
    const choix = [
        correct,
        'Une spirale carrée.',
        'Un seul grand carré.',
        'Une ligne de carrés posés côte à côte.',
    ];
    const texte = `<p><b>Le lutin trace un carré (côté de départ ${s0} pas), revient à son point de `
        + `départ, agrandit le côté de ${p} pas, puis recommence, ${n} fois en tout. `
        + 'Quelle figure obtient-il ?</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_FIGURE), elements_additionnels: imgElement(scriptCarres(s0, p, n)) });
    payload._meta.params = { s0, p, n };
    return payload;
};

// ═════════ Carte 4 — combien de motifs ? (saisie) ═════════

const MOTIF_NOMS = ['Brique', 'Étoile', 'Vague', 'Losange', 'Tuile'];

const genFriseMotifs = () => {
    const w = randInt(2, 8) * 10;
    const n = randInt(3, 9);
    const L = n * w;   // largeur exacte
    const nom = randomChoice(MOTIF_NOMS);
    const texte = `<p><b>Un motif « ${nom} » fait ${w} pas de large. Le lutin le trace, avance de `
        + `${w} pas, et recommence pour couvrir une frise de ${L} pas de large.</b></p>`
        + '<p>Combien de fois trace-t-il le motif ?</p>';
    const payload = makeSaisie(String(n), texte, opts(L_FRISE));
    payload._meta.params = { w, n, L, nom };
    return payload;
};

// ═════════ Carte 5 — le bon bloc dans la boucle (QCM) ═════════

const genMotifBloc = () => {
    const w = randInt(2, 8) * 10;
    const n = randInt(3, 9);
    const nom = randomChoice(MOTIF_NOMS);
    const correct = `le bloc « ${nom} », puis « avancer de ${w} pas »`;
    const choix = [
        correct,
        `seulement « avancer de ${w} pas »`,
        `seulement le bloc « ${nom} »`,
        '« effacer tout », puis « avancer »',
    ];
    const texte = `<p><b>Pour reproduire le motif « ${nom} » (large de ${w} pas) ${n} fois en frise, `
        + 'quel(s) bloc(s) placer À L\'INTÉRIEUR de la boucle « répéter '
        + `${n} fois » ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_MOTIF_BLOC));
    payload._meta.params = { w, n, nom };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    carres_valeurs_texte_trou_saisie: genCarresValeurs,
    carres_grand_texte_saisie: genCarresGrand,
    carres_figure_qcm_unique: genCarresFigure,
    frise_motifs_texte_saisie: genFriseMotifs,
    motif_bloc_qcm_unique: genMotifBloc,
};

const CARTES = [
    { id: 'carres_valeurs', label: L_VALEURS },
    { id: 'carres_grand', label: L_GRAND },
    { id: 'carres_figure', label: L_FIGURE },
    { id: 'frise_motifs', label: L_FRISE },
    { id: 'motif_bloc', label: L_MOTIF_BLOC },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
